import {
  functionalUpdate,
  SortingState,
  TableOptions,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import { useGetDisplayMarketplacePaymentsTableColumns } from 'src/tables/MarketplacePaymentsTable/useGetDisplayMarketplacePaymentsTableColumns';
import { Table } from 'src/tables/Table';
import { MarketplacePaymentsTableColumnId } from 'src/utils/columns/marketplacePayments/marketplacePaymentsColumnsUtils.types';
import { MarketplaceSalePayment, UserSetting } from 'src/WebApiController';

const getRowId = (row: MarketplaceSalePayment | null, index: number) =>
  (row?.id ?? index).toString();

interface PaymentsTableProps {
  tableData: MarketplaceSalePayment[] | null;
  tableSummary?: MarketplaceSalePayment | null;
}

export const MarketplacePaymentsTable = ({
  tableData,
  tableSummary,
}: PaymentsTableProps) => {
  const hasTableTotalsFeature = useUserHasTableTotalsFeature();

  const paymentsTableColumnConfig =
    useGetDisplayMarketplacePaymentsTableColumns();
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: MarketplacePaymentsTableColumnId.PaymentDate,
      desc: true,
    },
  ]);

  const options: Partial<TableOptions<MarketplaceSalePayment | null>> = useMemo(
    () => ({
      data: tableData ?? [],
      columns: Object.values(paymentsTableColumnConfig),
      state: {
        sorting,
        columnPinning: {
          right: [MarketplacePaymentsTableColumnId.Actions],
        },
      },
      meta: {
        summaryData: hasTableTotalsFeature ? tableSummary : null,
      },
      getRowId: getRowId,
      onSortingChange: (sortingUpdaterFn) => {
        const newSortVal = functionalUpdate(sortingUpdaterFn, sorting);
        setSorting(newSortVal);
      },
      sortingFns: {},
    }),
    [
      tableData,
      paymentsTableColumnConfig,
      sorting,
      tableSummary,
      hasTableTotalsFeature,
    ]
  );

  return (
    <ColumnResizingContextProvider
      userSettingId={UserSetting.MarketplacePaymentsColumnsWidth}
    >
      <Table
        options={options}
        tableLayout="fixed"
        withOuterPadding={true}
        useVirtuoso={false}
        usePaginationFooter={false}
        disableFooter={!hasTableTotalsFeature || !tableSummary}
        useStickyFooter
      />
    </ColumnResizingContextProvider>
  );
};
